import React, { useRef, useState, useEffect } from 'react';
import { Row, Col, Form, InputGroup, Alert, Dropdown } from 'react-bootstrap';
import { FaExclamationTriangle } from 'react-icons/fa';
import { FaScaleBalanced } from 'react-icons/fa6';
import { FaToggleOn, FaToggleOff } from 'react-icons/fa';
import './ProductDetailsForm.scss';

interface ProductDetailsFormProps {
  items: any[];
  itemName: string;
  setItemName: (val: string) => void;
  label: string;
  setLabel: (val: string) => void;
  price: string;
  setPrice: (val: string) => void;
  description: string;
  setDescription: (val: string) => void;
  currentItemId?: number | string; // Optional prop to identify the item being updated
  // Granel (by weight/volume) props
  isGranel?: boolean;
  setIsGranel?: (val: boolean) => void;
  unidadesMedida?: { id: number; name: string }[];
  selectedUnidadMedidaId?: number;
  setSelectedUnidadMedidaId?: (val: number) => void;
  selectedUnidadMedidaName?: string;
  setSelectedUnidadMedidaName?: (val: string) => void;
}

const ProductDetailsForm: React.FC<ProductDetailsFormProps> = ({
  items,
  itemName,
  setItemName,
  label,
  setLabel,
  price,
  setPrice,
  description,
  setDescription,
  currentItemId,
  // Granel props with defaults
  isGranel = false,
  setIsGranel = () => {},
  unidadesMedida = [],
  selectedUnidadMedidaId = 0,
  setSelectedUnidadMedidaId = () => {},
  selectedUnidadMedidaName = 'Seleccione unidad',
  setSelectedUnidadMedidaName = () => {}
}) => {
  const userTypedInternalName = useRef(false);
  const [showSpecialCharWarning, setShowSpecialCharWarning] = useState(false);
  const [showDuplicateNameWarning, setShowDuplicateNameWarning] = useState(false);

  // Special characters that should not be allowed in internal name
  const invalidChars = /[@{}ñáéíóúüç&]/gi;

  // Function to normalize special characters for internal name
  const normalizeForInternalName = (text: string): string => {
    return text
      .replace(/ñ/gi, 'n')        // Replace ñ with n
      .replace(/á/gi, 'a')        // Replace á with a
      .replace(/é/gi, 'e')        // Replace é with e
      .replace(/í/gi, 'i')        // Replace í with i
      .replace(/ó/gi, 'o')        // Replace ó with o
      .replace(/ú/gi, 'u')        // Replace ú with u
      .replace(/ü/gi, 'u')        // Replace ü with u
      .replace(/ç/gi, 'c')        // Replace ç with c
      .replace(/&/g, 'y')         // Replace & with y
      .replace(/[@{}]/g, '')      // Remove @, {, }
      .toLowerCase();
  };

  // Check for duplicate names in real time
  useEffect(() => {
    if (label.trim()) {
      const labelExists = items?.some(item => {
        // Skip checking against the current item being updated
        if (currentItemId && item.id === currentItemId) {
          return false;
        }
        return item.label?.trim().toLowerCase() === label.trim().toLowerCase();
      });
      setShowDuplicateNameWarning(labelExists);
    } else {
      setShowDuplicateNameWarning(false);
    }
  }, [label, items, currentItemId]);

  const handleLabelChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newLabel = e.target.value;
    setLabel(newLabel);
    
    if (!userTypedInternalName.current) {
      // Auto-generate internal name using the normalize function
      const cleanName = normalizeForInternalName(newLabel);
      setItemName(cleanName);
    }
  };

  const handleItemNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    
    // Check for invalid characters
    if (invalidChars.test(newValue)) {
      setShowSpecialCharWarning(true);
      // Normalize invalid characters instead of just removing them
      const cleanValue = normalizeForInternalName(newValue);
      setItemName(cleanValue);
      userTypedInternalName.current = cleanValue !== label;
      
      // Hide warning after 3 seconds
      setTimeout(() => setShowSpecialCharWarning(false), 3000);
    } else {
      setShowSpecialCharWarning(false);
      setItemName(newValue);
      userTypedInternalName.current = newValue !== label;
    }
  };

  return (
    <div className="product-details-form">
      <Row className="g-1">
        {/* Primary Row - Most Important Fields */}
        <Col lg={5} md={12}>
          <Form.Group className="form-field">
            <Form.Label className="field-label required">
              Nombre en carta
            </Form.Label>
            <Form.Control
              type="text"
              value={label}
              placeholder="Pizza Margarita"
              onChange={handleLabelChange}
              className="modern-input"
              required
            />
          </Form.Group>
        </Col>
        
        <Col lg={3} md={6}>
          <Form.Group className="form-field">
            <Form.Label className="field-label required">Valor</Form.Label>
            <InputGroup className="price-input-group">
              <InputGroup.Text className="currency-symbol">$</InputGroup.Text>
              <Form.Control
              type="text"
              value={price.replace(/\B(?=(\d{3})+(?!\d))/g, '.')}
              placeholder="0"
              onChange={(e) => {
                // Remove all non-digit characters
                const raw = e.target.value.replace(/\D/g, '');
                setPrice(raw);
              }}
              className="modern-input price-input"
              inputMode="numeric"
              pattern="[0-9]*"
              // price is optional; empty will be sent as 0
              />
            </InputGroup>
          </Form.Group>
        </Col>

        <Col lg={4} md={6}>
          <Form.Group className="form-field">
            <Form.Label className="field-label">
              Nombre interno
            </Form.Label>
            <Form.Control
              type="text"
              value={itemName}
              placeholder="pizza_margarita"
              onChange={handleItemNameChange}
              className="modern-input code-input"
              required
            />
          </Form.Group>
        </Col>
      </Row>

      {/* Warning Banners */}
      {showDuplicateNameWarning && (
        <Alert variant="danger" className="validation-banner mt-2">
          <div className="d-flex align-items-center">
            <FaExclamationTriangle />
            <div>
              <strong>Nombre duplicado:</strong> Ya existe un producto con este nombre. Por favor, usa un nombre diferente.
            </div>
          </div>
        </Alert>
      )}

      {showSpecialCharWarning && (
        <Alert variant="warning" className="validation-banner mt-2">
          <div className="d-flex align-items-center">
            <FaExclamationTriangle />
            <div>
              <strong>Caracteres no permitidos:</strong> El nombre interno no puede contener @, &, {}, tildes o ñ porque pueden causar problemas en las impresoras.
            </div>
          </div>
        </Alert>
      )}

      {/* Warning when price is zero (non-blocking) */}
      {Number(price) === 0 && !isGranel && (
        <Alert variant="warning" className="validation-banner mt-2">
          <div className="d-flex align-items-center">
            <FaExclamationTriangle />
            <div>
              <strong>Precio en 0:</strong> Este producto se enviará con precio 0. Esto no impedirá el envío del formulario, pero revisa si esto es intencional.
            </div>
          </div>
        </Alert>
      )}

      {/* Granel Configuration Section */}
      <Row className="g-1 mt-3">
        <Col xs={12}>
          <div className="granel-configuration-section">
            <div className="granel-toggle-container">
              <div className="granel-toggle-content">
                <div className="granel-toggle-info">
                  <div className="granel-toggle-title">
                    <FaScaleBalanced className="granel-icon" />
                    <span className="granel-title-text">Producto a granel</span>
                    <span className="granel-subtitle-inline">
                      {currentItemId 
                        ? 'El tipo de producto no se puede modificar después de la creación'
                        : 'Se vende por peso o volumen (kg, litros, etc.)'
                      }
                    </span>
                  </div>
                </div>
                <div className="config-toggle" onClick={() => {
                  if (currentItemId) return; // Prevent changes in update mode
                  const newValue = !isGranel;
                  setIsGranel(newValue);
                  if (!newValue) {
                    // Reset granel-specific fields when disabled
                    setPrice('');
                    setSelectedUnidadMedidaId(0);
                    setSelectedUnidadMedidaName('Seleccione unidad');
                  }
                }}>
                  {isGranel ? (
                    <FaToggleOn className={`toggle-icon ${currentItemId ? 'disabled' : 'active'}`} />
                  ) : (
                    <FaToggleOff className={`toggle-icon ${currentItemId ? 'disabled' : 'inactive'}`} />
                  )}
                </div>
              </div>
            </div>

            {isGranel && (
              <div className="granel-pricing-container">
                <div className="granel-pricing-preview">
                  Se venderá a 
                  <span className="price-highlight">
                    ${price ? Number(price).toLocaleString('es-CO') : '0'}
                  </span>
                  el 
                  <span className="unit-highlight">
                    {selectedUnidadMedidaName !== 'Seleccione unidad' ? selectedUnidadMedidaName : '[unidad]'}
                  </span>
                </div>
                
                <Row className="g-2 mt-2">
                  <Col md={6}>
                    <Form.Group className="form-field">
                      <Form.Label className="field-label required">Precio por unidad</Form.Label>
                      <InputGroup className="price-input-group">
                        <InputGroup.Text className="currency-symbol">$</InputGroup.Text>
                        <Form.Control
                          type="text"
                          value={price.replace(/\B(?=(\d{3})+(?!\d))/g, '.')}
                          placeholder="0"
                          onChange={(e) => {
                            // Remove all non-digit characters
                            const raw = e.target.value.replace(/\D/g, '');
                            setPrice(raw);
                          }}
                          className="modern-input price-input"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          required
                        />
                      </InputGroup>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group className="form-field">
                      <Form.Label className="field-label required">Unidad de medida</Form.Label>
                      <Dropdown className="unit-dropdown">
                        <Dropdown.Toggle 
                          variant="none" 
                          className="modern-input dropdown-toggle-custom"
                          id="unit-dropdown"
                        >
                          {selectedUnidadMedidaName}
                        </Dropdown.Toggle>
                        
                        <Dropdown.Menu className="unit-dropdown-menu">
                          {unidadesMedida.map((unit) => (
                            <Dropdown.Item
                              key={unit.id}
                              onClick={() => {
                                setSelectedUnidadMedidaId(unit.id);
                                setSelectedUnidadMedidaName(unit.name);
                              }}
                              active={selectedUnidadMedidaId === unit.id}
                            >
                              {unit.name}
                            </Dropdown.Item>
                          ))}
                        </Dropdown.Menu>
                      </Dropdown>
                    </Form.Group>
                  </Col>
                </Row>

                {/* Warning for granel without price or unit */}
                {(Number(price) === 0 || selectedUnidadMedidaId === 0) && (
                  <Alert variant="warning" className="validation-banner mt-2">
                    <div className="d-flex align-items-center">
                      <FaExclamationTriangle />
                      <div>
                        <strong>Configuración incompleta:</strong> 
                        {Number(price) === 0 && selectedUnidadMedidaId === 0 
                          ? ' Debes especificar el precio y seleccionar la unidad de medida.'
                          : Number(price) === 0
                          ? ' Debes especificar el precio por unidad.'
                          : ' Debes seleccionar la unidad de medida.'
                        }
                      </div>
                    </div>
                  </Alert>
                )}
              </div>
            )}
          </div>
        </Col>
      </Row>

      {/* Secondary Row - Description */}
      <Row className="g-1 mt-2">
        <Col xs={12}>
          <Form.Group className="form-field">
            <div className="description-header">
              <Form.Label className="field-label">Descripción</Form.Label>
              <div className="char-counter">
                {description.length}/1000
              </div>
            </div>
            <Form.Control
              as="textarea"
              rows={2}
              value={description}
              placeholder="Describe brevemente el producto..."
              onChange={(e) => setDescription(e.target.value)}
              className="modern-input description-input"
              maxLength={1000}
            />
          </Form.Group>
        </Col>
      </Row>
    </div>
  );
};

export default ProductDetailsForm;
